import { useState } from 'react'
import { exportFile, importFile, type ImportPreview } from '../api/actions'
import {
  useCreateKey,
  useCreateProject,
  useDeleteTag,
  usePatchKey,
  usePatchProject,
  useRenameTag,
  useTags,
} from '../api/hooks'
import { Modal } from '../components/Modal'
import type { LocaleInfo, Project } from '../api/types'
import { useUi } from '../store/ui'

// Fallback only -- every real source locale carries its own `plural_forms`
// from GET /api/locales; this is just what a still-loading locale list
// falls back to so the dialog never renders zero fields.
const FALLBACK_SOURCE_FORMS = ['one', 'other']

export function AddKeyDialog({
  platform,
  projectId,
  locales,
  onClose,
}: {
  platform: string
  projectId: string | undefined
  locales: LocaleInfo[]
  onClose: () => void
}) {
  const create = useCreateKey(platform, projectId)
  const pushToast = useUi((state) => state.pushToast)
  const [name, setName] = useState('')
  const [context, setContext] = useState('')
  const [tags, setTags] = useState('')
  const [isPlural, setIsPlural] = useState(false)
  const [value, setValue] = useState('')
  // The SOURCE locale's own required forms, never the full six-category CLDR
  // set: English only ever needs one/other, so offering zero/two/few/many
  // here is how junk "demo.*" test keys got zero/two/few/many source rows
  // that no exporter for English will ever place.
  const sourceForms = locales.find((locale) => locale.is_source)?.plural_forms ?? FALLBACK_SOURCE_FORMS
  const [plurals, setPlurals] = useState<Record<string, string>>(
    Object.fromEntries(sourceForms.map((form) => [form, ''])),
  )

  const submit = () => {
    create.mutate(
      {
        name: name.trim(),
        context: context.trim(),
        is_plural: isPlural,
        tags: tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        source_value: isPlural
          ? Object.fromEntries(Object.entries(plurals).filter(([, text]) => text.trim()))
          : value,
      },
      {
        onSuccess: (key) => {
          pushToast(
            'success',
            `Key ${key.name} created${key.ai_jobs_enqueued ? ` · ${key.ai_jobs_enqueued} AI job(s) queued` : ''}`,
          )
          onClose()
        },
      },
    )
  }

  return (
    <Modal
      title="Add key"
      description="The English source is written to the platform's default project; AI translation is queued for every enabled target locale."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || create.isPending || (!isPlural && !value.trim())}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create key'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="key-name">
            Key name
          </label>
          <input
            id="key-name"
            className="field mt-1 font-mono"
            placeholder="feed.empty_state.title"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={isPlural}
            onChange={(event) => setIsPlural(event.target.checked)}
          />
          Plural key (CLDR forms)
        </label>
        {isPlural ? (
          <div className="space-y-1.5">
            <span className="label">Source per form ({sourceForms.join(', ')})</span>
            {sourceForms.map((form) => (
              <div key={form} className="flex items-center gap-2">
                <span className="w-12 font-mono text-2xs text-ink-600">{form}</span>
                <input
                  className="field"
                  aria-label={`English source, ${form}`}
                  value={plurals[form] ?? ''}
                  onChange={(event) =>
                    setPlurals((current) => ({ ...current, [form]: event.target.value }))
                  }
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <label className="label" htmlFor="key-value">
              English source
            </label>
            <textarea
              id="key-value"
              className="field mt-1 min-h-[4rem]"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </div>
        )}
        <div>
          <label className="label" htmlFor="key-context">
            Context note (read by translators and the AI)
          </label>
          <textarea
            id="key-context"
            className="field mt-1 min-h-[3rem]"
            value={context}
            onChange={(event) => setContext(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="key-tags">
            Tags (comma separated)
          </label>
          <input
            id="key-tags"
            className="field mt-1"
            value={tags}
            onChange={(event) => setTags(event.target.value)}
          />
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

export function DescriptionDialog({
  platform,
  project,
  onClose,
}: {
  platform: string
  project: Project
  onClose: () => void
}) {
  const patch = usePatchProject(platform)
  const pushToast = useUi((state) => state.pushToast)
  const [description, setDescription] = useState(project.description ?? '')
  const [name, setName] = useState(project.name)

  return (
    <Modal
      wide
      title={`AI context — ${project.name}`}
      description="This exact text is sent to the AI with every translation request for this project. Describe the client's business, audience and tone."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={patch.isPending}
            onClick={() =>
              patch.mutate(
                { projectId: project.id, name, description },
                {
                  onSuccess: () => {
                    pushToast('success', 'Project context saved — future AI jobs will use it.')
                    onClose()
                  },
                },
              )
            }
          >
            {patch.isPending ? 'Saving…' : 'Save context'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="project-name">
            Project name
          </label>
          <input
            id="project-name"
            className="field mt-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {project.slug && (
            <p className="mt-1 text-2xs text-ink-600">
              Keyed to org domain <span className="font-mono text-ink-700">{project.slug}</span> —
              clients pulling with this domain get these overrides.
            </p>
          )}
        </div>
        <div>
          <label className="label" htmlFor="project-description">
            Client context for the AI
          </label>
          <textarea
            id="project-description"
            className="field mt-1 min-h-[14rem] font-normal leading-relaxed"
            placeholder="Who the client is, who reads the app, tone of voice, words to avoid, formality expectations…"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={1000}
          />
          <p className="mt-1 flex items-center justify-between text-2xs text-ink-600">
            <span>Sent verbatim as the AI system context. DeepL ignores it (no prompt support).</span>
            <span className="tabular-nums">{description.length}/1000</span>
          </p>
        </div>
        {patch.isError && (
          <p role="alert" className="text-xs text-rose-800">
            {(patch.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

export function NewProjectDialog({
  platform,
  onClose,
}: {
  platform: string
  onClose: () => void
}) {
  const create = useCreateProject(platform)
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  return (
    <Modal
      title="New client project"
      description="A client project overrides the platform default wording for one tchop org domain."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!slug.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { slug: slug.trim().toLowerCase(), name: name.trim() || slug.trim(), description },
                { onSuccess: onClose },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create project'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="project-slug">
            Org domain (project key)
          </label>
          <input
            id="project-slug"
            className="field mt-1 font-mono"
            placeholder="aok.de"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-project-name">
            Display name
          </label>
          <input
            id="new-project-name"
            className="field mt-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-project-description">
            Client context for the AI
          </label>
          <textarea
            id="new-project-description"
            className="field mt-1 min-h-[6rem]"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
        {create.isError && (
          <p role="alert" className="text-xs text-rose-800">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

export function ImportDialog({
  platform,
  project,
  locales,
  onClose,
}: {
  platform: string
  project: Project
  locales: LocaleInfo[]
  onClose: () => void
}) {
  const pushToast = useUi((state) => state.pushToast)
  const [file, setFile] = useState<File | null>(null)
  const [locale, setLocale] = useState(locales[0]?.code ?? 'en')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [busy, setBusy] = useState(false)

  const run = async (dryRun: boolean) => {
    if (!file) return
    setBusy(true)
    try {
      const result = await importFile({
        platform,
        projectId: project.id,
        locale,
        file,
        dryRun,
      })
      setPreview(result)
      if (!dryRun) {
        pushToast('success', `Imported: ${result.created} new, ${result.updated} updated.`)
        onClose()
      }
    } catch (error) {
      pushToast('error', `Import failed: ${(error as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      wide
      title="Import translations"
      description="Loco JSON, .strings, .xcstrings or strings.xml. German lands as manual so AI never overwrites it."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn" disabled={!file || busy} onClick={() => run(true)}>
            Dry run
          </button>
          <button
            className="btn btn-primary"
            disabled={!file || busy || !preview}
            onClick={() => run(false)}
          >
            Commit import
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="label" htmlFor="import-file">
              File
            </label>
            <input
              id="import-file"
              className="field mt-1"
              type="file"
              accept=".json,.strings,.xcstrings,.xml"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
                setPreview(null)
              }}
            />
          </div>
          <div className="w-40">
            <label className="label" htmlFor="import-locale">
              Locale
            </label>
            <select
              id="import-locale"
              className="field mt-1"
              value={locale}
              onChange={(event) => setLocale(event.target.value)}
            >
              {locales.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code}
                </option>
              ))}
            </select>
          </div>
        </div>
        {preview && (
          <div className="rounded border border-ink-200 bg-ink-50 p-2 text-xs">
            <p className="font-medium text-ink-800">
              {preview.format}: {preview.created} new · {preview.updated} updated ·{' '}
              {preview.unchanged} unchanged
            </p>
            <ul className="mt-1 space-y-0.5">
              {preview.samples.slice(0, 12).map((sample) => (
                <li key={sample.key} className="truncate font-mono text-2xs">
                  <span className="text-ink-600">{sample.key}</span>{' '}
                  {sample.old_value && <span className="text-ink-600 line-through">{sample.old_value}</span>}{' '}
                  <span className="text-ink-800">{sample.new_value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  )
}

export function ExportDialog({
  platform,
  project,
  locales,
  onClose,
}: {
  platform: string
  project: Project
  locales: LocaleInfo[]
  onClose: () => void
}) {
  const pushToast = useUi((state) => state.pushToast)
  const [locale, setLocale] = useState(locales.find((item) => !item.is_source)?.code ?? 'en')
  const [format, setFormat] = useState('json')
  const [busy, setBusy] = useState(false)

  const download = async () => {
    setBusy(true)
    try {
      const result = await exportFile({
        platform,
        clientSlug: project.is_default ? null : project.slug,
        locale,
        format,
      })
      const blob = new Blob([result.body], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = result.filename
      anchor.click()
      URL.revokeObjectURL(url)
      onClose()
    } catch (error) {
      pushToast('error', `Export failed: ${(error as Error).message}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Export"
      description="Resolved values: client override → default project → English source."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={download}>
            {busy ? 'Building…' : 'Download'}
          </button>
        </>
      }
    >
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="label" htmlFor="export-locale">
            Locale
          </label>
          <select
            id="export-locale"
            className="field mt-1"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
          >
            {locales.map((item) => (
              <option key={item.code} value={item.code}>
                {item.code}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="label" htmlFor="export-format">
            Format
          </label>
          <select
            id="export-format"
            className="field mt-1"
            value={format}
            onChange={(event) => setFormat(event.target.value)}
          >
            <option value="json">Web JSON</option>
            <option value="strings">iOS .strings</option>
            <option value="xcstrings">iOS .xcstrings</option>
            <option value="android_xml">Android strings.xml</option>
          </select>
        </div>
      </div>
    </Modal>
  )
}

export function RenameKeyDialog({
  platform,
  projectId,
  keyId,
  currentName,
  onClose,
}: {
  platform: string
  projectId: string | undefined
  keyId: string
  currentName: string
  onClose: () => void
}) {
  const patch = usePatchKey(platform, projectId)
  const [name, setName] = useState(currentName)
  const trimmed = name.trim()

  return (
    <Modal
      title={`Rename key — ${currentName}`}
      description="The key name is the identifier apps reference in source code (e.g. t('key.name'))."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!trimmed || trimmed === currentName || patch.isPending}
            onClick={() =>
              patch.mutate(
                { keyId, name: trimmed },
                {
                  onSuccess: () => {
                    onClose()
                  },
                },
              )
            }
          >
            {patch.isPending ? 'Renaming…' : 'Rename'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p
          role="alert"
          className="rounded border border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
        >
          <strong>This breaks every app build still deployed with the old name.</strong> The old
          name (<span className="font-mono">{currentName}</span>) keeps resolving at{' '}
          <span className="font-mono">/api/v1/pull</span> and <span className="font-mono">
            /api/v1/push
          </span>{' '}
          until every client redeploys against the new one — but any app that has NOT redeployed
          is still requesting the old string, so it will not see the app-side benefit of this
          rename (e.g. a code search for the old identifier) until it does. Do not rename a key
          casually.
        </p>
        <div>
          <label className="label" htmlFor="rename-key-name">
            New key name
          </label>
          <input
            id="rename-key-name"
            className="field mt-1 font-mono"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        {patch.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(patch.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

export function TagManagerDialog({
  platform,
  projectId,
  onClose,
}: {
  platform: string
  projectId: string | undefined
  onClose: () => void
}) {
  const tags = useTags(platform)
  const rename = useRenameTag(platform, projectId)
  const del = useDeleteTag(platform, projectId)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')

  return (
    <Modal
      wide
      title="Manage tags"
      description="Tags are per-platform key metadata; they are never part of any exported file, so renaming or deleting one never invalidates a client's cached pull."
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {tags.isLoading && <p className="text-xs text-ink-600">Loading tags…</p>}
      {tags.data && tags.data.length === 0 && (
        <p className="text-xs text-ink-600">No tags on this platform yet.</p>
      )}
      {tags.data && tags.data.length > 0 && (
        <ul className="divide-y divide-ink-100">
          {tags.data.map((row) => (
            <li key={row.tag} className="flex items-center gap-2 py-1.5 text-xs">
              {editing === row.tag ? (
                <>
                  <input
                    className="field flex-1 py-1 text-xs"
                    aria-label={`Rename tag ${row.tag}`}
                    value={draft}
                    autoFocus
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') setEditing(null)
                    }}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={!draft.trim() || rename.isPending}
                    onClick={() => {
                      rename.mutate(
                        { tag: row.tag, newName: draft.trim() },
                        { onSuccess: () => setEditing(null) },
                      )
                    }}
                  >
                    Save
                  </button>
                  <button className="btn" onClick={() => setEditing(null)}>
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span className="font-mono text-ink-800">{row.tag}</span>
                  <span className="text-2xs text-ink-600">
                    {row.count} key{row.count === 1 ? '' : 's'}
                  </span>
                  <div className="flex-1" />
                  <button
                    className="btn"
                    onClick={() => {
                      setEditing(row.tag)
                      setDraft(row.tag)
                    }}
                  >
                    Rename
                  </button>
                  <button
                    className="btn"
                    disabled={del.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove "${row.tag}" from all ${row.count} key(s)?`)) {
                        del.mutate(row.tag)
                      }
                    }}
                  >
                    Delete
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  )
}
